import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { getInvoices, getInvoice, markInvoicePaid, voidInvoice, getInvoicePdf, type InvoiceStatus } from '@/services'
import { AppButton, EmptyState, FilterChip, Skeleton, StatusBadge, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { formatDate, formatCurrency } from '@/lib/utils'

const FILTERS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Todas' },
  { id: 'paid', label: 'Pagadas' },
  { id: 'open', label: 'Abiertas' },
  { id: 'void', label: 'Anuladas' },
]

function InvoiceDetailSkeleton() {
  return (
    <>
      <div className="page-header" style={{ marginBottom: 18 }}>
        <div className="page-title-block" style={{ minWidth: 0 }}>
          <Skeleton width={150} height={22} style={{ marginBottom: 10 }} />
          <Skeleton width={220} height={34} />
          <Skeleton width={360} height={16} style={{ marginTop: 10 }} />
        </div>
        <div className="page-actions">
          <Skeleton width={118} height={32} radius={8} />
          <Skeleton width={124} height={32} radius={8} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <div className="card"><div style={{ padding: 28 }}><Skeleton height={330} /></div></div>
        <div className="card" style={{ alignSelf: 'flex-start' }}><div className="card-header"><h3>Pago</h3></div><div className="card-body"><Skeleton height={120} /></div></div>
      </div>
    </>
  )
}

// ---------- LIST ----------
export function InvoicesPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')

  const { data: result, isLoading } = useQuery({
    queryKey: ['invoices', status],
    queryFn: () => getInvoices({ status: status === 'all' ? undefined : (status as InvoiceStatus), pageSize: 100 }),
  })
  const all = result?.data || []
  const invoices = search
    ? all.filter((i) => [i.number, i.tenant_business_name, i.account_name].some((v) => (v || '').toLowerCase().includes(search.toLowerCase())))
    : all
  const totalPaid = all.filter((i) => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0)
  const totalOpen = all.filter((i) => i.status === 'open' || i.status === 'pending').reduce((s, i) => s + (i.total || 0), 0)

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Billing</div>
          <h1>Invoices</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={340} height={14} /> : `${result?.count || all.length} facturas · cobrado ${formatCurrency(totalPaid, 'ARS')} · pendiente ${formatCurrency(totalOpen, 'ARS')}`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar CSV</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="table-search">
            <Icon name="search" size={14} />
            <input placeholder="Buscar por número, tenant…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {FILTERS.map((f) => <FilterChip key={f.id} active={status === f.id} onClick={() => setStatus(f.id)} label={f.label} />)}
        </div>
        {isLoading ? (
          <TableSkeleton columns={7} rows={8} />
        ) : invoices.length === 0 ? (
          <EmptyState icon="file" title="Sin facturas" desc="No hay facturas con ese filtro." />
        ) : (
          <table className="table">
            <thead>
              <tr><th>Número</th><th>Tenant</th><th>Account</th><th style={{ textAlign: 'right' }}>Total</th><th>Status</th><th>Emitida</th><th>Pagada</th></tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} {...rowActivation(() => navigate(`/invoices/${inv.id}`))}>
                  <td><span className="code-chip">{inv.number || inv.id.slice(0, 8)}</span></td>
                  <td><span className="cell-primary">{inv.tenant_business_name || '—'}</span></td>
                  <td><span style={{ fontSize: 12.5 }}>{inv.account_name || '—'}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular cell-primary">{formatCurrency(inv.total || 0, inv.currency || 'ARS')}</span></td>
                  <td><StatusBadge status={inv.status} size="sm" /></td>
                  <td><span style={{ fontSize: 12 }}>{inv.issued_at ? formatDate(inv.issued_at) : '—'}</span></td>
                  <td>{inv.paid_at ? <span style={{ fontSize: 12 }}>{formatDate(inv.paid_at)}</span> : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

// ---------- DETAIL ----------
export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const { data: inv, isLoading } = useQuery({ queryKey: ['invoice', id], queryFn: () => getInvoice(id!), enabled: !!id })

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); qc.invalidateQueries({ queryKey: ['invoices'] }) }
  const paidMutation = useMutation({ mutationFn: () => markInvoicePaid(id!), onSuccess: () => { toast.success('Factura marcada pagada'); invalidate() }, onError: (e: any) => toast.error(e?.message || 'Error') })
  const voidMutation = useMutation({ mutationFn: () => voidInvoice(id!), onSuccess: () => { toast.success('Factura anulada'); invalidate() }, onError: (e: any) => toast.error(e?.message || 'Error') })
  const pdfMutation = useMutation({ mutationFn: () => getInvoicePdf(id!), onSuccess: (d) => { if (d.pdf_url) window.open(d.pdf_url, '_blank'); else toast.message(d.message || 'PDF no disponible') }, onError: (e: any) => toast.error(e?.message || 'Error') })

  if (isLoading || !inv) return <InvoiceDetailSkeleton />

  return (
    <>
      <div className="page-header" style={{ marginBottom: 18 }}>
        <div className="page-title-block" style={{ minWidth: 0 }}>
          <div className="row gap-2" style={{ marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Link to="/invoices" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>← Invoices</Link>
            <span style={{ color: 'var(--text-4)' }}>/</span>
            <StatusBadge status={inv.status} size="sm" />
          </div>
          <h1 style={{ fontSize: 26, fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '-0.01em' }}>{inv.number || inv.id.slice(0, 8)}</h1>
          <div className="subtitle" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span>Total:</span>
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{formatCurrency(inv.total || 0, inv.currency || 'ARS')}</span>
            <span className="muted">·</span>
            <span>emitida {inv.issued_at ? formatDate(inv.issued_at) : '—'}</span>
          </div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" size="sm" icon="download" onClick={() => pdfMutation.mutate()}>Descargar PDF</AppButton>
          {inv.status === 'open' || inv.status === 'pending' ? <AppButton variant="success" size="sm" icon="check" onClick={() => paidMutation.mutate()}>Marcar pagada</AppButton> : null}
          {inv.status !== 'void' && inv.status !== 'paid' ? <AppButton variant="secondary" size="sm" icon="x" onClick={() => voidMutation.mutate()}>Anular</AppButton> : null}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <div className="card">
          <div style={{ padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 22, letterSpacing: '-0.04em' }}>
                  bisne<span style={{ width: 6, height: 6, background: 'var(--bisne-green)', borderRadius: '50%' }} />
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Bisne SaaS</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="t-eyebrow">Factura</div>
                <div className="t-mono" style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>{inv.number || inv.id.slice(0, 8)}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Emitida {inv.issued_at ? formatDate(inv.issued_at) : '—'}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              <div>
                <div className="t-eyebrow" style={{ marginBottom: 8 }}>Facturado a</div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.account_name || '—'}</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{inv.account_email || ''}</div>
              </div>
              <div>
                <div className="t-eyebrow" style={{ marginBottom: 8 }}>Tenant</div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.tenant_business_name || '—'}</div>
                {inv.tenant_slug ? <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}><span className="code-chip">{inv.tenant_slug}</span></div> : null}
              </div>
            </div>

            <div style={{ marginLeft: 'auto', maxWidth: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}><span className="muted">Subtotal</span><span className="tabular">{formatCurrency(inv.subtotal || 0, inv.currency || 'ARS')}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}><span className="muted">Impuestos</span><span className="tabular">{formatCurrency(inv.tax || 0, inv.currency || 'ARS')}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid var(--border)', fontWeight: 700 }}><span>Total</span><span className="tabular">{formatCurrency(inv.total || 0, inv.currency || 'ARS')}</span></div>
            </div>
          </div>
        </div>

        <div className="card" style={{ alignSelf: 'flex-start' }}>
          <div className="card-header"><h3>Pago</h3></div>
          <div className="card-body">
            <dl className="dl" style={{ gridTemplateColumns: '110px 1fr' }}>
              <dt>Status</dt><dd><StatusBadge status={inv.status} size="sm" /></dd>
              <dt>Pagada</dt><dd>{inv.paid_at ? formatDate(inv.paid_at) : '—'}</dd>
              <dt>Método</dt><dd>{inv.payment_method || '—'}</dd>
              <dt>Referencia</dt><dd>{inv.payment_reference || '—'}</dd>
            </dl>
          </div>
        </div>
      </div>
    </>
  )
}
